import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EnvironmentInjector, Inject, numberAttribute, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ModalService, ToastType } from '@indice/ng-components';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { catchError, Observable, of, switchMap, tap } from 'rxjs';
import { Address, OrderResponse, ProofOfPayment, ServicesPaymentsApiService, UpdateOrderRequest, WayOfPayment } from 'src/app/core/services/payments-api-service';
import { mapOrderItemsToCheckoutProducts } from 'src/app/features/property-upload-wizard/ProductUtils/products';
import { CheckoutProduct } from 'src/app/features/property-upload-wizard/step-four/product-card/product-card.component';
import { CmsApiService } from 'src/app/services/cms-api.service';
import { UnikoServicesConfig, UnikoServiceTypes } from 'src/app/services/payments-api.service';
import { PortalApiService, PropertyDetails } from 'src/app/services/portal-api.service';
import { ToasterService } from 'src/app/services/toaster.service';
import { ProgressStepperComponent } from 'src/app/shared/components/progress-stepper/progress-stepper.component';
import { NavigationButtonsComponent } from '../navigation-buttons/navigation-buttons.component';
import { ExitModalComponent } from './exit-modal/exit-modal.component';
import { BuyerInfoModel, CardInfoModel, ProofOfPaymentOption } from './models';
import { StepBuyerInfoComponent } from "./step-buyer-info/step-buyer-info.component";
import { StepPaymentDetailsComponent } from "./step-payment-details/step-payment-details.component";
import { StepReviewComponent } from "./step-review/step-review.component";
import { buildSteps, getPreviousSteps } from './utils';
import { PackageComponent } from './package/package.component';
import { Location } from '@angular/common';


@Component({
  selector: 'app-checkout',
  standalone: true,
  templateUrl: './checkout.component.html',
  styleUrl: './checkout.component.css',
  imports: [CommonModule, ProgressStepperComponent, NavigationButtonsComponent, StepBuyerInfoComponent, StepReviewComponent, StepPaymentDetailsComponent, TranslocoDirective, PackageComponent]
})
export class CheckoutComponent implements OnInit, AfterViewInit {
  @ViewChild(StepBuyerInfoComponent) stepBuyerInfoComponent: StepBuyerInfoComponent | undefined;
  @ViewChild(StepPaymentDetailsComponent) stepPaymentDetailsComponent: StepPaymentDetailsComponent | undefined;
  @ViewChild(StepReviewComponent) stepReviewComponent: StepReviewComponent | undefined;
  protected Step: string = "Step1"
  protected stepIndex = 1;

  private defaultSteps: string[] = [
    "shared.checkout.buyerInfo",
    "shared.checkout.paymentDetails",
    "shared.checkout.overview",
  ];

  private StepsWithoutPayment: string[] = [
    "shared.checkout.overview",
  ];

  protected Steps: string[] = this.defaultSteps;
  protected step2Loaded: boolean = false;
  public orderNumber: string = '';
  protected order$: Observable<OrderResponse> | undefined;
  protected order: OrderResponse | undefined;
  public buyerInfoData: BuyerInfoModel | undefined;
  public cardData: CardInfoModel | undefined;
  public paymentDetailsData: any;
  public loading: boolean = false;
  public property?: PropertyDetails
  protected isServiceforDrafts?: boolean;
  protected isExclusiveProductTypeSelected?: boolean;
  protected selectedProducts?: CheckoutProduct[]
  private showCVV: boolean = false;

  protected proofOptionToProofEnum: Record<ProofOfPaymentOption, ProofOfPayment> = {
    [ProofOfPaymentOption.Receipt]: ProofOfPayment.Receipt,
    [ProofOfPaymentOption.Invoice]: ProofOfPayment.Invoice,
  };
  constructor(
    private _paymentsApiService: ServicesPaymentsApiService,
    private _modalService: ModalService,
    private cmsService: CmsApiService,
    private portalService: PortalApiService,
    private activatedRoute: ActivatedRoute,
    private _router: Router,
    private location: Location,
    private _translate : TranslocoService,
    @Inject(ToasterService) private _toastService: ToasterService,
    @Inject(EnvironmentInjector) private _envInjector: EnvironmentInjector,
  ) {

  }

  ngAfterViewInit() {
    this.scrollToTop();
  }

  ngOnInit(): void {
    this.order$ = this.activatedRoute.params.pipe(
      tap(t => this.loading = true),
      switchMap((params: Params) => {
        this.orderNumber = params['orderNumber'];
        return this._paymentsApiService.getOrderByNumber(this.orderNumber);
      })
    );
    this.order$.pipe(
      switchMap((res: OrderResponse) => {
        this.loading = false;
        this.order = res;
        const steps: Map<number, string> = this._router.lastSuccessfulNavigation?.extras?.state?.['data']
        const actualSteps = buildSteps({ stepMap: steps });
        if (steps) {
          this.Steps = actualSteps.stepNames ?? []
          this.stepIndex = actualSteps?.initialActiveStep?.stepIndex ?? 1
        }
        if (this.order?.total == 0) {
          this.Step = 'Step3';
          if (steps) {
            this.Steps = getPreviousSteps(steps).concat(this.StepsWithoutPayment);
          } else {
            this.Steps = this.StepsWithoutPayment;
          }
        }

        const exclusiveServiceTypes = [UnikoServiceTypes.propertyUpload, UnikoServiceTypes.unikoExclusive, UnikoServiceTypes.unikoPhotography, UnikoServiceTypes.photography];
        let isServiceForDrafts = false;
        let isExclusiveProductTypeSelected = false;

        for (const item of res.orderItems ?? []) {
          const utype = UnikoServicesConfig.find(f => f.sku === item.sku)?.type;

          if (utype) {
            if (exclusiveServiceTypes.includes(utype)) {
              isServiceForDrafts = true;
            }
            if (utype === UnikoServiceTypes.unikoExclusive) {
              isExclusiveProductTypeSelected = true;
            }
          }

          // Early exit if both conditions are met
          if (isServiceForDrafts && isExclusiveProductTypeSelected) {
            break;
          }
        }

        this.isServiceforDrafts = isServiceForDrafts;
        this.isExclusiveProductTypeSelected = isExclusiveProductTypeSelected;


        this.buyerInfoData = {
          email: res.billingAddress?.email,
          fullName: res.billingAddress?.fullName,
          address: res.billingAddress?.street,
          zipCode: res.billingAddress?.postalCode,
          city: res.billingAddress?.city,
          mobilePhone: res.billingAddress?.phoneNumber1,
          landLine: res.billingAddress?.phoneNumber2,
          proofOfPayment: ProofOfPaymentOption[res.invoiceType as keyof typeof ProofOfPaymentOption],
          invoiceData: {
            taxNumber: res.billingAddress?.taxNumber,
            companyName: res.billingAddress?.representativeName, //TODO: move this to a dedicated field. For now server does not support companyName
          }
        };

        this.cmsService.unicoProducts().pipe().subscribe(resp => {
          this.selectedProducts = mapOrderItemsToCheckoutProducts(res.orderItems ?? [], resp, this._envInjector)
        })
        const propertyId = res.orderItems?.map(x => x.propertyId)?.[0]

        this.portalService.getPropertyByIdOrCode(propertyId || "").pipe(
          tap(resp => {
            this.property = resp
          })
        ).subscribe()
        return of(res);
      }),
      catchError((err: any) => {
        this._toastService.showBanner(ToastType.Error, this._translate.translate('Failed to get order'), err.message || 'Unknown error', 10000);

        throw err;
      })
    ).subscribe({
      complete: () => { this.loading = false; },
      error: (err: any) => {
        console.error(err)
      },
    });
  }

  protected submitStep1(): void {
    if (this.stepBuyerInfoComponent?.onSubmit()) {
      this.updateOrderBilling().pipe(
        tap(t => {
          this.step2Loaded = true;
          if (t?.total == 0) {
            this.Goto("Step3");
          }
          else {
            this.Goto("Step2")
          }
          this.scrollToTop();
        })
      ).subscribe();
    }
  }
  protected submitStep2(): void {
    this.stepPaymentDetailsComponent?.updateSessionFromCardForm()
  }
  protected submitStep3(): void {
    if(this.stepReviewComponent?.onSubmit()) {      
      this.loading = true;
      if (this.order?.total == 0) {
        this._paymentsApiService.settlePay(this.order.number ?? '').subscribe({
          next: () => {
            this.loading = false;
            this._router.navigate([`/checkout/${this.order?.number ?? ''}/complete`]);
          },
          error: (err) => { 
            this._toastService.showBanner(ToastType.Error, 'Failed to update order', err.message || 'Unknown error', 10000); 
            this.loading = false;
          }
        });
  
      }
      else {
        var optionalParams = {
          fullScreenRedirect: true,
        };
        const orderNumber = this.order?.number ?? '';
        const transactionId = this.cardData?.transactionId ?? '';
        ThreeDS.authenticatePayer(orderNumber, transactionId, () => { }, optionalParams);
      }
    }
  }

  protected submitStep4(): void {
    throw new Error('Method not implemented.');
  }

  protected Goto(step: string): void {
    this.Step = step;
  }

  protected GoBack(): void {
    this.location.back()
  }

  protected handleCardData(cardData: CardInfoModel): void {
    this.cardData = cardData;
    this.Goto("Step3");
  }

  private updateOrderBilling(): Observable<OrderResponse> {
    const invoiceType: ProofOfPayment = this.mapProofOptionToProof(this.buyerInfoData?.proofOfPayment ?? ProofOfPaymentOption.Receipt);
    let taxNumber = undefined;
    let companyName = undefined;
    if (invoiceType === ProofOfPayment.Invoice) {
      taxNumber = this.buyerInfoData?.invoiceData?.taxNumber;
      companyName = this.buyerInfoData?.invoiceData?.companyName;
    }

    const requestBody = new UpdateOrderRequest({
      wayOfPayment: WayOfPayment.CreditCard,
      invoiceType: invoiceType,
      billingAddress: new Address({
        email: this.buyerInfoData?.email,
        fullName: this.buyerInfoData?.fullName,
        street: this.buyerInfoData?.address,
        postalCode: this.buyerInfoData?.zipCode,
        city: this.buyerInfoData?.city,
        phoneNumber1: this.buyerInfoData?.mobilePhone,
        phoneNumber2: this.buyerInfoData?.landLine ?? undefined,
        taxNumber: taxNumber,
        representativeName: companyName, //TODO: move this to a dedicated field. For now server does not support companyName
      })
    });

    return this._paymentsApiService.updateMyOrder(this.orderNumber, requestBody).pipe(
      catchError((err: any) => {
        this._toastService.showBanner(ToastType.Error, 'Failed to update order', err.message || 'Unknown error', 10000);

        throw err;
      }),
      tap((response: OrderResponse) => {
        this.order = response;
      })

    );
  }
  protected handleFormData(formData: BuyerInfoModel): void {
    this.buyerInfoData = formData;
  }
  protected OpenModalComponent(): void {
    const exitModal = this._modalService.show(ExitModalComponent, {
      animated: true,
      keyboard: true,
      class: 'h-dvh md:h-auto',
    });
    exitModal.onHidden?.subscribe((response: any) => {

    });
  }

  private mapProofOptionToProof(value: ProofOfPaymentOption): ProofOfPayment {
    var rs = ProofOfPayment.Unspecified;
    switch (value) {
      case ProofOfPaymentOption.Invoice: {
        rs = ProofOfPayment.Invoice
        break;
      }
      case ProofOfPaymentOption.Receipt: {
        rs = ProofOfPayment.Receipt
        break;
      }
      default: {
        rs = ProofOfPayment.Unspecified
        break;
      }
    }

    return rs;
  }

  scrollToTop() {
    // this.scrollableDiv.nativeElement.scrollTo({ top: 0, behavior: 'smooth' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  displayDiscount(product: CheckoutProduct) {
    return product?.discount && product.discount > 0;
  }
}
